import type { DocumentVectorsDto } from '@dbrain/shared';
import { Alert, Button } from '@dbrain/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client.js';
import { useCompanyZone } from '../../api/hooks.js';
import { DOCUMENT_VECTORS_KEY, documentVectorsNotices } from './document-vectors-model.js';

/**
 * Why the company's documents have no vectors (Phase 15g, D-253), under Onay politikaları → Belge araması, where the
 * embedding service is explained and only the company's admins come: "Gömme servisi yanıt vermiyor (500 ·
 * Connection error)", the last and the next attempt, how many documents wait, and "Şimdi yeniden dene". Nothing
 * shows while vectors are made as they should be.
 */
export function DocumentVectorsStatus() {
  const { t, i18n } = useTranslation();
  const zone = useCompanyZone();
  const queryClient = useQueryClient();
  const status = useQuery({
    queryKey: DOCUMENT_VECTORS_KEY,
    queryFn: () => api<DocumentVectorsDto>('GET', '/api/tenant/document-vectors'),
    // closer while something waits: the next attempt and its result show without a reload
    refetchInterval: (query) => (query.state.data?.pause || query.state.data?.documents ? 30_000 : 300_000),
  });
  const retry = useMutation({
    mutationFn: () => api<DocumentVectorsDto>('POST', '/api/tenant/document-vectors/retry'),
    onSuccess: (data) => queryClient.setQueryData(DOCUMENT_VECTORS_KEY, data),
  });
  // a status beside the choice: it never stands in the way of the card, also when it cannot be read
  if (!status.data) return null;
  const notices = documentVectorsNotices(status.data, t, i18n.language, zone);
  if (notices.length === 0) return null;
  return (
    <div className="stack" data-testid="document-vectors">
      {notices.map((notice) =>
        notice.kind === 'indexing' ? (
          <p key={notice.kind} className="muted" data-testid="document-vectors-indexing">
            {notice.title}
          </p>
        ) : (
          <Alert
            key={notice.kind}
            tone="warning"
            title={notice.title}
            data-testid={`document-vectors-${notice.kind}`}
            action={
              notice.retry ? (
                <Button size="sm" variant="secondary" busy={retry.isPending} onClick={() => retry.mutate()} data-testid="document-vectors-retry">
                  {t('policies.documentVectors.retry')}
                </Button>
              ) : undefined
            }
          >
            {notice.lines.map((line) => (
              <p key={line}>{line}</p>
            ))}
          </Alert>
        ),
      )}
      {retry.isSuccess ? (
        <p className="muted" role="status" data-testid="document-vectors-retried">
          {t('policies.documentVectors.retried')}
        </p>
      ) : null}
    </div>
  );
}
